/**
 * Aanmaken en wijzigen van één bedrag-regel.
 *
 * De rekenkern leest `categorie`, `subcategorie` en `bedrag_type` als sleutels,
 * niet als vrije tekst — welke combinatie wat doet staat in
 * `subsidie-evaluatie-core.ts:520-600`. De hints hieronder zijn daaruit
 * overgenomen zodat een beheerder niet hoeft te raden waarom een nieuw bedrag
 * op EUR 0 blijft staan.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Info } from 'lucide-react';
import type { SubsidieBedrag } from '@/hooks/useSubsidieRegelingen';
import { BEDRAG_TYPES, BEDRIJFSGROOTTES, type BedragInvoer } from '@/hooks/useSubsidieBeheer';

/** Wat de rekenkern met elk bedrag_type doet, in één zin per soort. */
const TYPE_UITLEG: Record<SubsidieBedrag['bedrag_type'], string> = {
  vast: 'Bedrag per laadstation. De rekenkern telt de stations waarvan vermogen en type binnen de subcategorie vallen (bijv. "ac_11_22" of "dc_50_150") en vermenigvuldigt.',
  per_kwh: 'Bedrag per kWh batterijcapaciteit. Werkt alleen bij categorie "batterij"; "max eenheden" is het kWh-plafond.',
  per_kw: 'Bedrag per kW. De rekenkern kent voor dit type vandaag geen tak — het levert EUR 0 op.',
  percentage: 'Fractie (0,4 = 40%) van de eigen grondslag van dit onderdeel. Welke intake-velden dat zijn hangt aan de categorie; ontbreekt dat veld, dan blijft het onderdeel op EUR 0 met een toelichting.',
  aftrek: 'Fiscale aftrek: fractie van de grondslag, waarna het VPB-tarief erover gaat. Per categorie vuurt hoogstens één schijf.',
};

function alsGetal(ruw: string): number | null {
  const tekst = ruw.trim().replace(',', '.');
  if (tekst === '') return null;
  const getal = Number(tekst);
  return Number.isFinite(getal) ? getal : null;
}

export function BedragDialog({
  open,
  onOpenChange,
  regelingId,
  bedrag,
  volgendeSortOrder,
  onBewaar,
  isBezig,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regelingId: string;
  /** `null` = nieuw bedrag. */
  bedrag: SubsidieBedrag | null;
  volgendeSortOrder: number;
  /**
   * Wat hier wordt ingevuld gaat NIET rechtstreeks naar de database: het
   * paneel rekent er eerst mee door wat het met de lopende dossiers doet
   * (`subsidie-voorvertoning.ts`) en schrijft pas na bevestiging. Vandaar dat
   * elk veld hier open staat — ook categorie, subcategorie en soort bedrag,
   * die in ronde één op slot stonden. Een slot op de kolomnaam zag de drie
   * gemeten verhogingen niet; de voorvertoning ziet ze alle drie.
   */
  onBewaar: (invoer: { id?: string; velden: BedragInvoer }) => void;
  isBezig: boolean;
}) {
  const [categorie, setCategorie] = useState('');
  const [subcategorie, setSubcategorie] = useState('');
  const [bedragType, setBedragType] = useState<SubsidieBedrag['bedrag_type']>('vast');
  const [waarde, setWaarde] = useState('');
  const [maxBedrag, setMaxBedrag] = useState('');
  const [maxEenheden, setMaxEenheden] = useState('');
  const [minEenheden, setMinEenheden] = useState('');
  const [bedrijfsgrootte, setBedrijfsgrootte] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    if (!open) return;
    setCategorie(bedrag?.categorie ?? '');
    setSubcategorie(bedrag?.subcategorie ?? '');
    setBedragType(bedrag?.bedrag_type ?? 'vast');
    setWaarde(bedrag?.bedrag != null ? String(bedrag.bedrag) : '');
    setMaxBedrag(bedrag?.max_bedrag != null ? String(bedrag.max_bedrag) : '');
    setMaxEenheden(bedrag?.max_eenheden != null ? String(bedrag.max_eenheden) : '');
    setMinEenheden(bedrag?.min_eenheden != null ? String(bedrag.min_eenheden) : '');
    setBedrijfsgrootte(bedrag?.bedrijfsgrootte ?? '');
    setOpmerkingen(bedrag?.opmerkingen ?? '');
    setSortOrder((bedrag as { sort_order?: number } | null)?.sort_order ?? volgendeSortOrder);
  }, [open, bedrag, volgendeSortOrder]);

  const bedragWaarde = alsGetal(waarde);
  const nieuwMaxBedrag = alsGetal(maxBedrag);
  const fouten: string[] = [];

  /*
   * Eén grens die géén kwestie van rechten is maar van rekenkunde, en die
   * migratie 083 daarom als CHECK in de database zet: een maximum is leeg of
   * groter dan nul, nooit precies nul.
   *
   * Waarom dat een grens is: `calculateAmount` toetst `if (b.max_bedrag)` —
   * truthiness. Een maximum van 0 is NOT NULL maar falsy, dus de rij verhuist
   * stil naar het gedeelde percentageplafond en ontsnapt aan zijn eigen
   * maximum. Gemeten op een dossier waar de kap bijt: EUR 300.000 -> EUR
   * 600.000. Wie geen maximum wil, laat het veld leeg.
   */
  if (nieuwMaxBedrag !== null && nieuwMaxBedrag <= 0) {
    fouten.push(
      'Een maximum van 0 (of lager) bestaat niet: de rekenkern leest dat als "geen maximum" en haalt deze '
      + 'rij onder zijn eigen plafond vandaan. Laat het veld leeg als er geen maximum is.',
    );
  }
  if (categorie.trim() === '') fouten.push('Een categorie is verplicht; de rekenkern groepeert bedragen erop.');
  if (bedragWaarde === null) fouten.push('Vul een bedrag in (getal).');
  if ((bedragType === 'percentage' || bedragType === 'aftrek') && bedragWaarde !== null && bedragWaarde > 1) {
    fouten.push(`Een ${bedragType} is een fractie: 0,4 betekent 40%. ${bedragWaarde} zou ${bedragWaarde * 100}% opleveren.`);
  }

  const waarschuwingen: string[] = [];
  if (bedragType === 'per_kw') {
    waarschuwingen.push('De rekenkern heeft geen tak voor "per_kw" (subsidie-evaluatie-core.ts:524-600). Dit bedrag telt nergens mee.');
  }
  if (bedragType === 'per_kwh' && categorie.trim() !== 'batterij') {
    waarschuwingen.push('"per_kwh" wordt alleen berekend als de categorie exact "batterij" is; met een andere categorie blijft het bedrag EUR 0.');
  }

  function opslaan() {
    if (fouten.length > 0 || isBezig || bedragWaarde === null) return;
    onBewaar({
      id: bedrag?.id,
      velden: {
        regeling_id: regelingId,
        categorie: categorie.trim(),
        subcategorie: subcategorie.trim() === '' ? null : subcategorie.trim(),
        bedrag_type: bedragType,
        bedrag: bedragWaarde,
        max_bedrag: alsGetal(maxBedrag),
        max_eenheden: alsGetal(maxEenheden),
        min_eenheden: alsGetal(minEenheden),
        bedrijfsgrootte: bedrijfsgrootte === '' ? null : bedrijfsgrootte,
        opmerkingen: opmerkingen.trim() === '' ? null : opmerkingen.trim(),
        sort_order: sortOrder,
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{bedrag ? 'Bedrag wijzigen' : 'Nieuw bedrag'}</DialogTitle>
          <DialogDescription>
            Eén rij is één tarief. Categorie en subcategorie zijn sleutels voor de rekenkern, geen vrije tekst.
          </DialogDescription>
        </DialogHeader>

        <p className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Na "Opslaan" zie je eerst wat deze wijziging met de lopende dossiers doet — welk dossier van
            welk bedrag naar welk bedrag gaat. Verschuift er niets, dan slaat hij meteen op.
          </span>
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Categorie</Label>
              <Input value={categorie} onChange={e => setCategorie(e.target.value)} placeholder="bijv. batterij, laadinfra, realisatie" />
            </div>
            <div className="space-y-2">
              <Label>Subcategorie (optioneel)</Label>
              <Input value={subcategorie} onChange={e => setSubcategorie(e.target.value)} placeholder="bijv. dc_50_150, categorie_f_g" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Soort bedrag</Label>
              <Select value={bedragType} onValueChange={v => setBedragType(v as SubsidieBedrag['bedrag_type'])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BEDRAG_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{bedragType === 'percentage' || bedragType === 'aftrek' ? 'Fractie (0-1)' : 'Bedrag'}</Label>
              <Input value={waarde} onChange={e => setWaarde(e.target.value)} placeholder={bedragType === 'percentage' ? '0.4' : '85'} />
            </div>
          </div>

          <p className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            {TYPE_UITLEG[bedragType]}
          </p>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Max. bedrag</Label>
              <Input value={maxBedrag} onChange={e => setMaxBedrag(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Max. eenheden</Label>
              <Input value={maxEenheden} onChange={e => setMaxEenheden(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Min. eenheden</Label>
              <Input value={minEenheden} onChange={e => setMinEenheden(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Bedrijfsgrootte (optioneel)</Label>
              <Select value={bedrijfsgrootte || 'alle'} onValueChange={v => setBedrijfsgrootte(v === 'alle' ? '' : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle groottes</SelectItem>
                  {BEDRIJFSGROOTTES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Volgorde</Label>
              <Input type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value) || 0)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Opmerkingen</Label>
            <Textarea value={opmerkingen} onChange={e => setOpmerkingen(e.target.value)} rows={2} />
          </div>

          {waarschuwingen.map(w => (
            <div key={w} className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
          {fouten.map(f => (
            <div key={f} className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{f}</span>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={fouten.length > 0 || isBezig}>Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
